<template>
  <b-form v-if="response" >
    <b-container fluid>
      <loading-view v-if="loading" />
      <b-form-row >
        <b-col lg="2" md="4" sm="12">
          <b-form-group id="fob" label="FOB:" label-for="input-fob">
            <b-form-select  size="sm"
              inputId="input-fob"
              v-model="response.fob"
              placeholder="Enter FOB"
              :options="fobs"
            ></b-form-select>
          </b-form-group>
        </b-col>

        <b-col lg="3" md="4" sm="12">
          <b-form-group id="ref_no" label="Reference Number:" label-for="input-ref_no">
            <b-form-input size="sm"
              id="input-ref_no"
              v-model="response.ref_no"
              placeholder="(optional)"
            ></b-form-input>
          </b-form-group>
        </b-col>
        
        <b-col lg="3" md="4" sm="12">
          <b-form-group id="expiration_ts" label="Expiration:" label-for="input-expiration_ts">
            <b-form-datepicker size="sm"
              id="input-expiration_ts"
              v-model="response.expiration_ts"
              placeholder="Enter Expiration"
              :min="willAdviseMinDate" 
              :state="expirationState"
              locale="en-US"
              ref="expiration"
            ></b-form-datepicker>
          </b-form-group>
        </b-col>
        
        <b-col lg="2" md="6" sm="12">
          <b-form-group id="order_minimum_cost" label="Order Minimum:" label-for="input-order_minimum_cost">
            <b-input-group prepend="$" size="sm">
              <b-form-input size="sm"
                v-bind:id="'input-order_minimum_cost'"
                v-model="response.order_minimum_cost"
                type="number"
                step="any"
                placeholder="(optional)"
                :number="true"
              ></b-form-input>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col lg="2" md="6" sm="12">
          <b-form-group id="line_minimum_cost" label="Line Minimum:" label-for="input-line_minimum_cost">
            <b-input-group prepend="$" size="sm">
              <b-form-input size="sm"
                v-bind:id="'input-line_minimum_cost'"
                v-model="response.line_minimum_cost"
                type="number"
                step="any"
                placeholder="(optional)"
                :number="true"
              ></b-form-input>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-form-row>

      <additional-cost-form 
        v-bind:response="response"
        v-bind:additional_costs="header_additional_costs"
        v-bind:id="request.id"
        v-bind:caption="'Add special costs for this RFQ'"
        v-on:addAdditionalCost="addAdditionalCost" 
        v-on:removeAdditionalCost="removeAdditionalCost"
      />

      <!-- LIST OF RFQ LINES -->
      <div role="tablist">
        <b-card no-body class="mb-1" v-for="(request_line, line_index) in request.lines" :key="request_line.id">          
            <line-form 
              v-bind:request="request_line"
              v-bind:response="response.lines[line_index]"
              v-bind:coos="coos"
              v-bind:additional_costs="line_additional_costs"
              v-bind:no_quote_classifications="no_quote_classifications"
              v-bind:line_disposition_options="line_disposition_options"
              v-bind:line_condition_options="line_condition_options"
              v-bind:line_index="line_index"
              v-on:addQuantity="addQuantity" 
              v-on:removeQuantity="removeQuantity"
            />
        </b-card>
      </div>
      <!-- END OF LIST OF RFQ LINES -->



      <!-- FORM FOOTER -->

      <b-form-row>
        <b-col lg="5" md="6" sm="12">
          <b-form-group id="comments" label="Comments:" label-for="input-comments">
            <b-form-textarea 
              id="input-comments"
              v-model="response.comments"
              placeholder="(optional)"
              rows="4"
              wrap="hard"
            ></b-form-textarea>
          </b-form-group>
        </b-col>


        <!-- Validation Errors -->
        <b-col lg="4" md="6" sm="12">
          <div v-if="validationErrors.length">
            <b-form-group id="comments" :label="'Found ' + validationErrors.length + ' Input Errors:'" label-for="input-comments">
              <div class="validationErrorDiv">
                <div v-for="(error, idx) in validationErrors" :key="idx"  >
                  <div> {{error}}</div>
                </div>
            </div>
          </b-form-group>
          </div>
        </b-col>

        <b-col lg="3" md="6" sm="12"  align-self="start" style="padding-top: 30px;">
          
          <div class="d-flex">
            <input type="checkbox" style="margin-right: 5px; margin-top: 6px;" v-model="response.requirements_checked"/>
            <label for="checkbox" class="ml-auto mb-0">I acknowledge opportunity requirements and any exceptions have been listed in the comment box</label>
          </div>
          
          
          <b-button 
            block
            variant="success"            
            v-on:click="submitForm"
            v-bind:disabled="loading"
            v-b-tooltip.hover.v-secondary title="Review and submit your quote"
            style="margin-top: 5px;"
          >Submit
          </b-button>
        </b-col>
      </b-form-row>
    </b-container>
  </b-form>
</template>

<script>
import LineForm from './FormLine';
import AdditionalCostForm from './FormAdditionalCost';
import LoadingView from '@/components/common/LoadingView';
import {validateModel} from '@/components/rfqs/ValidationRules.js';
import { datadogLogs } from '@datadog/browser-logs';

const PortalApi = require("@brightoncromwell/portal-api");
const optionsApiInstance = new PortalApi.OptionsApi();

export default {
  name: 'rfq-form',
  props: [
    'loading',
    'request',
    'response',
  ],
  components: {
    'line-form': LineForm,
    'loading-view': LoadingView,
    'additional-cost-form': AdditionalCostForm
  },
  computed: {
    no_quote: function() {
      return !this.response.lines.filter(x => x.quantities.length).length;
    },

    expirationState: function() {
      // We only need an expiration date if ANY lines are being quoted. 
      var state = false;
      var hasQuotedLines = this.response.lines.some(l => l.no_quote_classification == null && l.will_advise_dt == null);
      if(hasQuotedLines == false) { state = true; }  // if nothing is being quoted, no need for expiration
      var noQuoteOrWillAdviseSelected = this.response.lines.some(l => l.no_quote_classification != null || l.will_advise_dt != null);
      if(noQuoteOrWillAdviseSelected == true && hasQuotedLines == false) { return null; }
      else
      {
        // otherwise, some lines are quoted and we need that expiration
        const today = new Date(new Date().getFullYear(), new Date().getMonth() , new Date().getDate());
        var expDate = new Date(this.response.expiration_ts);
        //console.log("expDate = " + expDate);
        //console.log("today = " + today);
        //console.log("expDate > today = " + (expDate > today) )
        state = (this.response.expiration_ts != null && expDate > today );  // non-blank and greater than "today"
      }
      return state;
    }
  },
  data() {
    const today = new Date(new Date().getFullYear(),new Date().getMonth() , new Date().getDate())
    const todayDate = new Date(today)

    return {
      validationErrors: [],
      fobs: [],
      coos: [],
      no_quote_classifications: [],
      line_disposition_options: [],
      line_condition_options: [],
      header_additional_costs: [],
      line_additional_costs: [],
      activeLine: 0,
      defaultDisposition: 0,
      lineDispositionOptions: [
        {text: 'Quoting', value: 0 },
        {text: 'No-Quote', value: 1},
        {text: 'Decide later...', value: 2},
      ],
      willAdviseMinDate: todayDate
    };
  },

  watch: {
    request: {
      handler(newRequest) {
        if (newRequest) {
          this.getLineDispositionOptions();
        }
      },
      immediate: true
    }
  },

  methods: {
    submitForm: function() {
      //console.log('submit!')
      this.validationErrors = validateModel(this.response);

      if (this.validationErrors.length) {
          datadogLogs.logger.error('submitForm() validation errors', { validationErrors: this.validationErrors });
          return;
        }
        datadogLogs.logger.info('submitForm() validation errors', { quoteId: this.response.id });
        this.$emit('submit');
    },
    getFobs: function() {
      optionsApiInstance.optionsFobsGet((error, data, response) => {
        if (error) {
          console.error(error);
          datadogLogs.logger.error('getFobs() failed', { error: error });
          this.$emit('onError', response)
        } else {
          let options = [{ value: '', text: '(unknown)' }];
          data.forEach((item) => options.push({ value: item, text: item }));
          this.fobs = options;
        }
      });
    },
    getCoos: function() {
      optionsApiInstance.optionsCoosGet((error, data, response) => {
        if (error) {
          console.error(error);
          datadogLogs.logger.error('getCoos() failed', { error: error });
          this.$emit('onError', response)
        } else {
          let options = [{ value: '', text: '(unknown)' }];
          data.forEach((item) => options.push({ value: item, text: item }));
          this.coos = options;
        }
      });
    },
    getNoQuoteClassifications: function() {
      optionsApiInstance.optionsNoQuoteClassificationsGet((error, data, response) => {
        if (error) {
          console.error(error);
          datadogLogs.logger.error('getNoQuoteClassifications() failed', { error: error });
          this.$emit('onError', response)
        } else {
          let options = [{ value: null, text: 'Please Provide No-Quote Reason' }];
          data.forEach((item) => options.push({ value: item, text: item }));
          this.no_quote_classifications = options;
        }
      });
    },
    getHeaderAdditionalCosts: function() {
      optionsApiInstance.optionsHeaderAdditionalCostsGet((error, data, response) => {
        if (error) {
          console.error(error);
          datadogLogs.logger.error('getHeaderAdditionalCosts() failed', { error: error });
          this.$emit('onError', response)
        } else {
          let options = [{ value: null, text: '(unknown)' }];
          data.forEach((item) => options.push({ value: item, text: item }));
          this.header_additional_costs = options;
        }
      });
    },
    getLineAdditionalCosts: function() {
      optionsApiInstance.optionsLineAdditionalCostsGet((error, data, response) => {
        if (error) {
          console.error(error);
          datadogLogs.logger.error('getLineAdditionalCosts() failed', { error: error });
          this.$emit('onError', response)
        } else {
          let options = [{ value: null, text: '(unknown)' }];
          data.forEach((item) => options.push({ value: item, text: item }));
          this.line_additional_costs = options;
        }
      });
    },
    getLineDispositionOptions: function() {
      var options = [
        {text: 'Providing quote', value: 0 },
        {text: 'No-Quoting', value: 1},
        {text: 'Will Advise...', value: 2},
      ];

      let partEntityId = null;
      if (this.request && this.request.lines) {
        partEntityId = this.request.lines[0].part_details[0].part_entity_leaf_id;
      }

      if (partEntityId && this.request.lines.every(line => line.part_details[0].part_entity_leaf_id === partEntityId)) {
        options = options.filter(option => option.value !== 2);
      }

      this.line_disposition_options = options;
    },
    getLineConditionOptions: function() {
      var options = [
        {text: 'Factory New', value: 'NEW'},
        {text: 'Surplus', value: 'SURPLUS'},
        {text: 'Refurbished', value: 'REFAB'},
      ];

      this.line_condition_options = options;
    },
    addQuantity: function(line_index) {
      const response_line_quantity = {}
      response_line_quantity.condition = "NEW"; // default all new quantity-conditions to "Factory New"
      //datadogLogs.logger.info('addQuantity() clicked', { error: error });

      if (this.request.lines[line_index].custom_fields) {
          response_line_quantity.custom_fields = {}
          this.request.lines[line_index].custom_fields.forEach((request_line_custom_fields) => {
          response_line_quantity.custom_fields[request_line_custom_fields] = ''
        });
      }

      this.response.lines[line_index].quantities.push(response_line_quantity);
      this.response.lines[line_index].no_quote_classification = null;
    },
    removeQuantity: function(line_index, quantity_index) {
      this.response.lines[line_index].quantities.splice(quantity_index, 1);
    },
    addAdditionalCost: function() {
      this.response.additional_costs.push({ per_unit: true });
    },
    removeAdditionalCost: function(additional_cost_index) {
      this.response.additional_costs.splice(additional_cost_index, 1);
    },
  },
  created() {
    this.getFobs();
    this.getCoos();
    this.getNoQuoteClassifications();
    this.getHeaderAdditionalCosts();
    this.getLineAdditionalCosts();
    this.getLineDispositionOptions();
    this.getLineConditionOptions();
  },
  mounted() {
    this.$refs.expiration.state = this.expirationState;
  }
}
</script>

<style scoped>
.validationErrorDiv {
   padding: 8px;
  border-radius: 5px;
  border: 1px solid lightgray;
  color: darkred;
  background-color: #ffeeee;
}

.form-group {
  margin-bottom: 0.75rem;
}
</style>